/**
 * Main Application - BVAL Track & Field Scoring
 * Mobile-first interface for scoring head-to-head dual meets
 */

import './scoring.css'
import { initFirebaseScoring, saveMeet, subscribeToMeets, createEventsForMeet, subscribeToEvents, saveResults, subscribeToResults, deleteMeet } from './firebase-scoring.js'
import { EVENTS, getEventName, computeMeetScores } from './engine.js'

// --- CONSTANTS ---
const MOUNT_HAMILTON_SCHOOLS = [
  'Branham',
  'Evergreen Valley',
  'Leigh',
  'Leland',
  'Pioneer',
  'Santa Teresa',
  'Westmont',
  'Willow Glen'
]

// Assign consistent colors to schools conceptually (0-3)
let SCHOOL_COLORS = {}

// --- STATE ---
let db = null
let currentMeets = []
let activeMeet = null
let activeDivision = 'varsity_boys'
let currentEvents = []
let currentResults = []
let activeScoringEvent = null
let unsubMeets = null
let unsubEvents = null
let unsubResults = null

// Offline caching support
const CACHE_PREFIX = 'mana_track_'

// --- DOM ELEMENTS ---
const appEl = document.getElementById('app')

// --- INITIALIZATION ---
async function init() {
  db = initFirebaseScoring()
  
  // Load cached data first for offline support
  loadCachedData()
  
  // Start router
  window.addEventListener('hashchange', handleRoute)
  handleRoute()

  // Subscribe to meets live
  if (db) {
    unsubMeets = subscribeToMeets((meets) => {
      currentMeets = meets
      saveToCache('meets', meets)
      if (window.location.hash === '' || window.location.hash === '#/') {
        renderMeetList()
      }
      
      // Update active meet if we're on a meet screen
      if (activeMeet) {
        const updated = meets.find(m => m.id === activeMeet.id)
        if (updated) {
          activeMeet = updated
        } else {
          // Meet was deleted
          window.location.hash = '/'
        }
      }
    })
  }
}

// --- OFFLINE CACHING ---
function saveToCache(key, data) {
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(data))
  } catch (e) {
    console.warn('Cache write failed', e)
  }
}

function loadFromCache(key) {
  try {
    const data = localStorage.getItem(CACHE_PREFIX + key)
    return data ? JSON.parse(data) : null
  } catch (e) {
    console.warn('Cache read failed', e)
    return null
  }
}

function loadCachedData() {
  const cachedMeets = loadFromCache('meets')
  if (cachedMeets) {
    currentMeets = cachedMeets
  }
}

// --- ROUTING ---
function handleRoute() {
  const hash = window.location.hash || '#/'
  
  if (hash === '#/') {
    renderMeetList()
  } else if (hash === '#/new') {
    renderMeetSetup()
  } else if (hash.startsWith('#/meet/')) {
    const meetId = hash.split('/')[2].split('?')[0]
    loadMeet(meetId)
  } else if (hash.startsWith('#/score/')) {
    const eventId = hash.split('/')[2]
    renderEventScoring(eventId)
  }
}

// --- VIEWS ---

/**
 * Helper to wrap content in standard layout
 */
function renderLayout({ title, subtitle = '', backRoute = null, content, tabs = null }) {
  let backBtn = ''
  if (backRoute) {
    backBtn = `<button class="header-back" onclick="window.location.hash='${backRoute}'">‹</button>`
  } else {
    backBtn = `<div style="width: 24px;"></div>` // Spacer for flex centering
  }

  let tabHtml = ''
  if (tabs) {
    tabHtml = `
      <div class="nav-tabs">
        ${tabs.map(t => `
          <button class="nav-tab ${t.active ? 'active' : ''}" onclick="window.location.hash='${t.route}'">
            ${t.label}
          </button>
        `).join('')}
      </div>
    `
  }

  appEl.innerHTML = `
    <div class="header">
      ${backBtn}
      <div style="flex:1; text-align:center; overflow:hidden;">
        <div class="header-title">${title}</div>
        ${subtitle ? `<div class="header-subtitle">${subtitle}</div>` : ''}
      </div>
      <div style="width: 24px;"></div> <!-- Spacer -->
    </div>
    ${tabHtml}
    <div class="container">
      ${content}
    </div>
  `
}

/**
 * 1. Meet List View
 */
function renderMeetList() {
  let content = `
    <div style="padding: 16px 0; display:flex; justify-content: space-between; align-items: center;">
      <h2 style="font-size: 1.2rem; margin: 0;">Dual Meets</h2>
      <button class="btn btn-primary" style="padding: 8px 16px; font-size: 0.9rem;" onclick="window.location.hash='#/new'">+ New Meet</button>
    </div>
  `

  if (currentMeets.length === 0) {
    content += `
      <div class="empty-state">
        <div class="empty-state-icon">🏃</div>
        <div class="empty-state-text">No meets created yet.<br>Create one to start scoring.</div>
      </div>
    `
  } else {
    content += '<div class="meet-list" style="margin-top: 12px;">'
    currentMeets.forEach(meet => {
      // Format schools string smartly
      let schoolsStr = meet.schools.join(' vs ')
      if (schoolsStr.length > 35) {
        schoolsStr = meet.schools.map(s => s.substring(0, 3).toUpperCase()).join(' v ')
      }
      
      let statusClass = meet.status === 'setup' ? 'setup' : (meet.status === 'live' ? 'live' : 'finished')
      let statusText = meet.status.toUpperCase()

      content += `
        <div class="meet-card" onclick="window.location.hash='#/meet/${meet.id}'">
          <div class="meet-card-info">
            <div class="meet-card-name">${schoolsStr}</div>
            <div class="meet-card-meta">
              ${new Date(meet.date).toLocaleDateString([], {month: 'short', day: 'numeric', year: 'numeric'})} 
              <span class="meet-status ${statusClass}">${statusText}</span>
            </div>
          </div>
          <div class="meet-card-arrow">›</div>
        </div>
      `
    })
    content += '</div>'
  }

  renderLayout({
    title: 'ManaScoring',
    content
  })
}

/**
 * 2. Meet Setup View
 */
function renderMeetSetup() {
  const dateStr = new Date().toISOString().split('T')[0] // YYYY-MM-DD
  
  let content = `
    <div class="section-title">Meet Details</div>
    
    <div class="form-group">
      <label class="form-label">Meet Date</label>
      <input type="date" id="setup-date" class="form-input" value="${dateStr}">
    </div>

    <div class="form-group">
      <label class="form-label">Venue (Optional)</label>
      <input type="text" id="setup-venue" class="form-input" placeholder="e.g. Evergreen Valley High School">
    </div>

    <div class="divider"></div>
    <div class="section-title">Select Schools (2 to 4)</div>
    
    <div class="school-check-list" id="school-list">
      ${MOUNT_HAMILTON_SCHOOLS.map((school, idx) => `
        <div class="school-check" data-school="${school}" onclick="this.classList.toggle('checked'); updateSetupSaveBtn()">
          <div class="school-check-box"></div>
          <div class="school-check-name">${school}</div>
        </div>
      `).join('')}
    </div>

    <div class="action-bar" style="position: sticky; bottom: 0; background: var(--bg-primary); padding-top: 16px;">
      <button class="btn btn-primary" id="btn-save-meet" disabled style="width: 100%" onclick="handleCreateMeet()">Create Meet & Events</button>
    </div>
  `

  renderLayout({
    title: 'New Dual Meet',
    backRoute: '#/',
    content
  })
}

// Made global for the inline onclick handlers
window.updateSetupSaveBtn = function() {
  const selected = document.querySelectorAll('.school-check.checked').length
  const btn = document.getElementById('btn-save-meet')
  if (btn) {
    btn.disabled = selected < 2 || selected > 4
    if (selected > 4) {
      showToast('Maximum 4 schools allowed')
    }
  }
}

window.handleCreateMeet = async function() {
  const date = document.getElementById('setup-date').value
  const venue = document.getElementById('setup-venue').value
  const schoolEls = document.querySelectorAll('.school-check.checked')
  
  const schools = Array.from(schoolEls).map(el => el.getAttribute('data-school'))
  
  if (schools.length < 2 || schools.length > 4) return

  const btn = document.getElementById('btn-save-meet')
  btn.disabled = true
  btn.innerText = 'Creating...'

  try {
    // 1. Create meet record
    const meetName = schools.join(' vs ')
    const meet = await saveMeet({
      name: meetName,
      date,
      venue,
      schools,
      status: 'live'
    })

    if (!meet) throw new Error('Failed to create meet')

    // 2. Create all 34 events (17 for VB, 17 for VG)
    await createEventsForMeet(meet.id, 'varsity_boys', EVENTS)
    await createEventsForMeet(meet.id, 'varsity_girls', EVENTS)

    // Assign colors to schools for this meet for the picker
    schools.forEach((s, i) => { SCHOOL_COLORS[s] = i })

    showToast('Meet created successfully')
    window.location.hash = `#/meet/${meet.id}`
  } catch (err) {
    console.error(err)
    showToast('Error creating meet')
    btn.disabled = false
    btn.innerText = 'Create Meet & Events'
  }
}

/**
 * 3. Load active meet data and setup subscriptions
 */
function loadMeet(meetId) {
  // Find meet from memory or wait for sub
  activeMeet = currentMeets.find(m => m.id === meetId)
  
  if (!activeMeet && !db) {
    // Offline and not in memory — try cache
    const cached = loadFromCache('meets')
    if (cached) activeMeet = cached.find(m => m.id === meetId)
  }

  if (!activeMeet && currentMeets.length > 0) {
    // Meet not found
    window.location.hash = '#/'
    return
  }
  
  // Set up colors
  if (activeMeet) {
    SCHOOL_COLORS = {}
    activeMeet.schools.forEach((s, i) => { SCHOOL_COLORS[s] = i })
  }

  // Subscribe to events for this meet+division
  if (unsubEvents) unsubEvents()
  if (unsubResults) unsubResults()

  // First check offline cache
  const cachedEvents = loadFromCache(`events_${meetId}_${activeDivision}`)
  if (cachedEvents) currentEvents = cachedEvents
  const cachedResults = loadFromCache(`results_${meetId}`)
  if (cachedResults) currentResults = cachedResults

  // Setup real-time listeners
  if (db) {
    unsubEvents = subscribeToEvents(meetId, activeDivision, (events) => {
      currentEvents = events
      saveToCache(`events_${meetId}_${activeDivision}`, events)
      // Re-render if we are on the split/list view
      if (window.location.hash === `#/meet/${meetId}`) {
        renderEventList()
      }
    })

    unsubResults = subscribeToResults(meetId, (results) => {
      currentResults = results
      saveToCache(`results_${meetId}`, results)
      // Re-render if we are on the status view
      if (window.location.hash === `#/meet/${meetId}?tab=status`) {
        renderMeetStatus()
      } else if (window.location.hash === `#/meet/${meetId}`) {
        // Results update might change event styling (○ to ●)
        renderEventList()
      }
    })
  }

  // Check URL params for tab
  const params = new URLSearchParams(window.location.hash.split('?')[1])
  if (params.get('tab') === 'status') {
    renderMeetStatus()
  } else {
    renderEventList()
  }
}

// Expose division switch to window for HTML handlers
window.switchDivision = function(div) {
  if (activeDivision === div) return
  activeDivision = div
  // Reload data for this division
  if (activeMeet) {
    loadMeet(activeMeet.id)
  }
}

/**
 * 4. Event List View (Inside a meet)
 */
function renderEventList() {
  if (!activeMeet) {
    renderLayout({ title: 'Loading...', content: 'Loading meet...' })
    return
  }

  const schoolsStr = activeMeet.schools.join(' vs ')
  const tabs = [
    { label: 'Events', route: `#/meet/${activeMeet.id}`, active: true },
    { label: 'Scoreboard', route: `#/meet/${activeMeet.id}?tab=status`, active: false }
  ]

  let content = `
    <div class="division-toggle">
      <div class="division-btn ${activeDivision === 'varsity_boys' ? 'active' : ''}" onclick="switchDivision('varsity_boys')">Varsity Boys</div>
      <div class="division-btn ${activeDivision === 'varsity_girls' ? 'active' : ''}" onclick="switchDivision('varsity_girls')">Varsity Girls</div>
    </div>
    <div class="divider" style="margin-top: 8px;"></div>
  `

  if (currentEvents.length === 0) {
    content += `<div style="text-align:center; padding: 20px; color: var(--text-muted);">Loading events...</div>`
  } else {
    content += '<div class="event-list">'
    currentEvents.forEach(ev => {
      // Check if event has results
      const hasResults = currentResults.some(r => r.eventId === ev.id)
      const statusClass = hasResults ? 'scored' : ''
      const typeBadge = ev.eventType === 'relay' ? '<span class="event-type-badge relay">Relay</span>' : '<span class="event-type-badge">Indiv</span>'
      const displayName = getEventName(EVENTS.find(e => e.code === ev.eventCode), activeDivision)

      content += `
        <div class="event-card ${statusClass}" onclick="window.location.hash='#/score/${ev.id}'">
          <div class="event-order">${ev.sortOrder}</div>
          <div class="event-name">${displayName}</div>
          ${typeBadge}
          <div class="event-status ${statusClass}"></div>
        </div>
      `
    })
    content += '</div>'
    
    // Add delete meet button at the very bottom
    content += `
      <div class="divider" style="margin-top: 32px"></div>
      <button class="btn btn-secondary" style="width: 100%; color: var(--error); border-color: rgba(255, 68, 68, 0.3);" onclick="confirmDeleteMeet('${activeMeet.id}')">
        Delete Meet
      </button>
    `
  }

  renderLayout({
    title: schoolsStr,
    subtitle: new Date(activeMeet.date).toLocaleDateString(),
    backRoute: '#/',
    tabs,
    content
  })
}

window.confirmDeleteMeet = async function(id) {
  if (confirm('Are you sure you want to delete this meet and all its scores? This cannot be undone.')) {
    try {
      await deleteMeet(id)
      showToast('Meet deleted')
      window.location.hash = '#/'
    } catch (e) {
      showToast('Error deleting meet')
    }
  }
}

/**
 * 5. Event Scoring View
 */
let scoringState = [] // Working copy of places for the active event

function renderEventScoring(eventId) {
  if (!activeMeet || currentEvents.length === 0) {
    // Navigated here directly on load, push back to list
    window.location.hash = '#/'
    return
  }

  activeScoringEvent = currentEvents.find(e => e.id === eventId)
  if (!activeScoringEvent) return

  // Find base event info
  const baseEvent = EVENTS.find(e => e.code === activeScoringEvent.eventCode)
  const displayName = getEventName(baseEvent, activeDivision)
  const isRelay = activeScoringEvent.eventType === 'relay'
  const divName = activeDivision === 'varsity_boys' ? 'Varsity Boys' : 'Varsity Girls'

  // Load existing results into state if any
  const existing = currentResults.filter(r => r.eventId === eventId)
  
  if (existing.length > 0 && scoringState.length === 0) {
    for (let i = 0; i < 12; i++) {
      const placeNum = i + 1
      const atPlace = existing.filter(r => r.place === placeNum)
      if (atPlace.length > 0) {
        scoringState[i] = { place: placeNum, school: atPlace.map(r => r.school) }
      } else {
        scoringState[i] = { place: placeNum, school: null }
      }
    }
  } else if (scoringState.length === 0) {
    for (let i = 0; i < 12; i++) {
      scoringState.push({ place: i + 1, school: null })
    }
  }

  let content = `
    <div class="scoring-header">
      <div class="scoring-event-name">${displayName}</div>
      <div class="scoring-event-type">${divName} · ${isRelay ? 'Winner takes 5 pts' : '5-3-1 pts'}</div>
    </div>
  `

  // Individual UI: 12 places
  let activeIndex = scoringState.findIndex(r => r.school === null || (Array.isArray(r.school) && r.school.length === 0))
  if (activeIndex === -1) activeIndex = 0 // all full

  content += `<div class="place-list">`
  for (let i = 0; i < 12; i++) {
    const p = scoringState[i] || { place: i+1, school: null }
    
    let schoolDisplay = ''
    if (p.school) {
      if (Array.isArray(p.school)) {
         schoolDisplay = p.school.length > 0 
           ? p.school.join(' • ') + `<span class="tie-badge">${p.school.length}-way tie</span>`
           : '<span class="place-school empty">Tap to assign...</span>'
      } else {
         schoolDisplay = p.school
      }
    } else {
      schoolDisplay = '<span class="place-school empty">Tap to assign...</span>'
    }

    const isActive = i === activeIndex
    const placeWord = i === 0 ? 'first' : i === 1 ? 'second' : i === 2 ? 'third' : ''
    
    const ptsDisplay = isRelay 
       ? (i === 0 ? '5pts' : '')
       : (i < 3 ? ['5', '3', '1'][i] + 'pts' : '')

    content += `
      <div class="place-row ${isActive ? 'active scoring-position' : i < 3 || (isRelay && i===0) ? 'scoring-position' : ''}" 
           id="place-row-${i}" 
           onclick="setActivePlace(${i})">
        <div class="place-number ${placeWord}">${i+1}</div>
        <div class="place-school ${!p.school ? 'empty' : ''}">${schoolDisplay}</div>
        <div class="place-points">${ptsDisplay}</div>
        ${p.school && (!Array.isArray(p.school) || p.school.length > 0) ? `
          <button class="place-clear" onclick="event.stopPropagation(); clearPlace(${i})">×</button>
        ` : '<div style="width: 28px;"></div>'}
      </div>
    `
  }
  content += `</div>`

  // Fixed school picker at bottom
  content += `
    <div style="position: sticky; bottom: 0; background: var(--bg-primary); padding-top: 12px; z-index: 10;">
      <div class="section-title" style="display:flex; justify-content: space-between; align-items: center;">
        <span>Assign Place <span id="active-place-indicator" style="color: var(--accent); font-size: 1rem; margin-left: 4px;">${activeIndex+1}</span></span>
        <span style="font-size: 0.7rem; font-weight: 400; text-transform: none;">Tap twice for ties</span>
      </div>
      
      <div class="school-picker">
        ${activeMeet.schools.map((school, i) => {
          const activeRes = scoringState[activeIndex]
          const isSelected = activeRes && Array.isArray(activeRes.school) && activeRes.school.includes(school)
          
          return `
            <button class="school-btn ${isSelected ? 'selected' : ''}" data-color="${i}" onclick="assignToActivePlace('${school}')">
              ${school}
            </button>
          `
        }).join('')}
      </div>
    </div>
  `

  // Action buttons
  content += `
    <div class="action-bar" style="margin-top: 24px; position: relative; z-index: 5;">
      <button class="btn btn-secondary" onclick="window.location.hash='#/meet/${activeMeet.id}'">Cancel</button>
      <button class="btn btn-primary" onclick="saveEventScores()">Save Scores</button>
    </div>
  `

  renderLayout({
    title: activeMeet.schools.join(' vs '),
    backRoute: `#/meet/${activeMeet.id}`,
    content
  })

  // Hacky: attach active index to window so school buttons know what to update
  window.currentActivePlace = scoringState.findIndex(r => !r.school || (Array.isArray(r.school) && r.school.length === 0)) || 0

}

function getSchoolColorBtn(index) {
  const colors = ['#FF6B6B', '#4ECDC4', '#FFE66D', '#A78BFA']
  return colors[index % colors.length]
}

// Global UI handlers for scoring
window.setActivePlace = function(index) {
  window.currentActivePlace = index
  // Re-render whole view to update highlights (fast enough on modern phones)
  renderEventScoring(activeScoringEvent.id)
  
  // Try to scroll the active row into view if it's hidden under the fixed picker
  setTimeout(() => {
    const row = document.getElementById(`place-row-${index}`)
    if (row) {
      const rect = row.getBoundingClientRect()
      if (rect.bottom > window.innerHeight - 250) { // Approx picker height
        row.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, 50)
}

window.assignToActivePlace = function(school) {
  const idx = window.currentActivePlace
  const entry = scoringState[idx]
  
  if (!entry.school) {
    // Empty, just add it
    entry.school = [school]
  } else if (Array.isArray(entry.school)) {
    // Existing array, toggle it (supports ties)
    if (entry.school.includes(school)) {
      // Remove it
      entry.school = entry.school.filter(s => s !== school)
    } else {
      // Add tie
      entry.school.push(school)
    }
  }

  // If we just added the first school to this place, auto-advance to next empty place
  if (entry.school && entry.school.length === 1 && !entry.school.startedEmpty) {
     entry.school.startedEmpty = false // prevent infinite leaping
     const nextEmpty = scoringState.findIndex(r => !r.school || r.school.length === 0)
     if (nextEmpty !== -1) {
       window.currentActivePlace = nextEmpty
     }
  }

  renderEventScoring(activeScoringEvent.id)
}

window.clearPlace = function(index) {
  scoringState[index].school = null
  window.currentActivePlace = index
  renderEventScoring(activeScoringEvent.id)
}

window.handleRelayScore = function(school) {
  // Relays are simple: index 0 is the winner
  scoringState[0].school = school
  renderEventScoring(activeScoringEvent.id)
}

window.saveEventScores = async function() {
  if (!activeScoringEvent || !activeMeet) return

  // Format state back to Firebase format: [{place, school}]
  const resultsToSave = []
  
  for (const entry of scoringState) {
    if (entry.school && Array.isArray(entry.school)) {
      // Individual places can have arrays due to ties
      for (const tiedSchool of entry.school) {
        resultsToSave.push({ place: entry.place, school: tiedSchool })
      }
    }
  }

  try {
    await saveResults(activeMeet.id, activeScoringEvent.id, resultsToSave)
    showToast('Scores saved!')
    scoringState = [] // Clear working memory
    window.location.hash = `#/meet/${activeMeet.id}`
  } catch (err) {
    console.error(err)
    showToast('Error saving: ' + err.message)
  }
}

/**
 * 6. Meet Status / Scoreboard View
 */
function renderMeetStatus() {
  if (!activeMeet) return

  const schoolsStr = activeMeet.schools.join(' vs ')
  const tabs = [
    { label: 'Events', route: `#/meet/${activeMeet.id}`, active: false },
    { label: 'Scoreboard', route: `#/meet/${activeMeet.id}?tab=status`, active: true }
  ]

  let content = `
    <div class="division-toggle">
      <div class="division-btn ${activeDivision === 'varsity_boys' ? 'active' : ''}" onclick="switchDivision('varsity_boys')">Varsity Boys</div>
      <div class="division-btn ${activeDivision === 'varsity_girls' ? 'active' : ''}" onclick="switchDivision('varsity_girls')">Varsity Girls</div>
    </div>
    <div class="divider" style="margin-top: 8px;"></div>
  `

  if (currentResults.length === 0) {
    content += `
      <div class="empty-state">
        <div class="empty-state-icon">📊</div>
        <div class="empty-state-text">No scores entered yet.<br>Go to Events to start scoring.</div>
      </div>
    `
  } else {
    // 1. Organize results by event Id to feed engine
    const resultsByEvent = {}
    currentEvents.forEach(e => {
      // Find all results across all divisions to allow full cross-meet computation
      // But only process active division's events
      if (e.division === activeDivision) {
        resultsByEvent[e.id] = {
          eventCode: e.eventCode,
          eventType: e.eventType,
          results: currentResults.filter(r => r.eventId === e.id).map(r => ({ place: r.place, school: r.school }))
        }
      }
    })

    // 2. Run Head-to-Head Engine
    const scores = computeMeetScores(activeMeet.schools, resultsByEvent)

    // 3. Render Cards
    content += `<div class="section-title">Head-to-Head Matchups</div>`
    
    scores.forEach(matchup => {
      // Determine leaders
      const isLeadA = matchup.scoreA > matchup.scoreB
      const isLeadB = matchup.scoreB > matchup.scoreA

      // Calculate bar widths (relative to total)
      const total = matchup.scoreA + matchup.scoreB || 1 // prevent div 0
      const pctA = (matchup.scoreA / total) * 100
      const pctB = (matchup.scoreB / total) * 100

      content += `
        <div class="score-card">
          <div class="score-card-header">
            
            <div class="score-school">
              <div class="score-school-name">${matchup.schoolA}</div>
              <div class="score-school-points ${isLeadA ? 'leading' : ''}">${matchup.scoreA}</div>
            </div>
            
            <div class="score-vs">VS</div>
            
            <div class="score-school">
              <div class="score-school-name">${matchup.schoolB}</div>
              <div class="score-school-points ${isLeadB ? 'leading' : ''}">${matchup.scoreB}</div>
            </div>

          </div>
          
          <div class="score-bar">
            <div class="score-bar-fill-a" style="width: ${pctA}%"></div>
            <div class="score-bar-fill-b" style="width: ${pctB}%"></div>
          </div>
        </div>
      `
    })

  }

  renderLayout({
    title: schoolsStr,
    subtitle: new Date(activeMeet.date).toLocaleDateString(),
    backRoute: '#/',
    tabs,
    content
  })
}

// --- UTILS ---
function showToast(message) {
  let toast = document.getElementById('toast')
  if (!toast) {
    toast = document.createElement('div')
    toast.id = 'toast'
    toast.className = 'toast'
    document.body.appendChild(toast)
  }
  
  toast.innerText = message
  toast.classList.add('show')
  
  setTimeout(() => {
    toast.classList.remove('show')
  }, 3000)
}

// Start app
init()
